import { useEffect } from 'react';
import { useParams, Outlet } from 'react-router-dom';
import { PermissionEnum } from '@ui/src';
import { useAppSelector } from '@store';
import CustomerFees from '../../../components/common/tariff/fees';
import tariffSelector from '../store/slices/tariff/tariffs-selector';
import { OrganizationType } from '../types';
import { GeneralInfo } from '../../individual/types';
import { CorporateGeneralInfo } from '../../corporate/types';
import MarginDetails from './parts/margin-details';
import useTariff from './hooks/use-tariff';
import ChangeTariffPanel from './parts/change-tariff-panel';
import CustomMargins from './parts/custom-margins';

import cn from './styles.module.css';

const CustomerTariffs = ({ type }: { type: OrganizationType }) => {
  const {
    fees,
    searchValue,
    customMargins,
    customMarginsErrorsList,
    rateSources,
    customMarginsPagination,
    defaultMargin,
  } = useAppSelector((state) => state.customerTariffs);
  const { baseInformation, baseInformationIsLoading } = useAppSelector(
    (state) => state.individualCustomers.info.general,
  );
  const {
    baseInformation: corporateGeneralInfo,
    baseInformationIsLoading: corporateBaseInformationIsLoading,
  } = useAppSelector((state) => state.corporateCustomers.info.general);
  const { riskScoreRanges: riskScoreRangesDictionaries, baseCurrency } =
    useAppSelector((state) => state.customersDictionaries);
  const { currencies: currenciesList, activePermissions } = useAppSelector(
    (state) => state.root,
  );
  const { tariffsList } = useAppSelector(tariffSelector);

  const tariffInfo =
    type === OrganizationType.INDIVIDUAL
      ? baseInformation
      : corporateGeneralInfo;

  const { customerId } = useParams();

  const {
    isLoading,
    setSelectedTariffId,
    selectedTariffId,
    panelIsOpen,
    handleClose,
    tariffChangeInProgress,
    assignTariffToCustomerAction,
    allMarginsVisible,
    formattedData,
    sort,
    onRowsSelect,
    riskLevel,
    deleteInProgress,
    onDelete,
    deleteIsSuccess,
    setPanelIsOpen,
    setAllMarginsVisible,
    setSelectedCurrency,
    selectedCurrency,
    showMoreCurrencies,
    setShowMoreCurrencies,
    setMenuOnFocus,
    menuOnFocus,
    setSeletedMarginId,
    setMarginPanelIsOpen,
    selectedMarginId,
    marginPanelIsOpen,
  } = useTariff({
    type,
    riskScoreRangesDictionaries,
    tariffInfo,
    fees,
    customerId,
    tariffsList,
    rateSources,
    baseCurrency,
    currenciesList,
  });

  useEffect(() => {
    let title: string;

    const companyName = localStorage.getItem('companyName');

    if (type === OrganizationType.INDIVIDUAL) {
      if (
        (tariffInfo as Partial<GeneralInfo>)?.firstName ||
        (tariffInfo as Partial<GeneralInfo>)?.lastName
      ) {
        title = `${(tariffInfo as Partial<GeneralInfo>)?.firstName} ${(tariffInfo as Partial<GeneralInfo>)?.lastName}: Tariff`;
      } else {
        title = 'Tariff';
      }
    } else if ((tariffInfo as Partial<CorporateGeneralInfo>)?.name) {
      title = `${(tariffInfo as Partial<CorporateGeneralInfo>)?.name}: Tariff`;
    } else {
      title = 'Tariff';
    }

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [tariffInfo]);

  return (
    <>
      <ChangeTariffPanel
        isLoading={isLoading}
        tariffsList={tariffsList}
        setSelectedTariffId={setSelectedTariffId}
        selectedTariffId={selectedTariffId}
        type={type as 'INDIVIDUAL' | 'CORPORATE'}
        panelIsOpen={panelIsOpen}
        handleClose={handleClose}
        tariffChangeInProgress={tariffChangeInProgress}
        assignTariffToCustomerAction={assignTariffToCustomerAction}
        searchValue={searchValue}
      />
      {!allMarginsVisible && (
        <CustomerFees
          isLoading={
            type === OrganizationType.INDIVIDUAL
              ? (baseInformationIsLoading as boolean)
              : (corporateBaseInformationIsLoading as boolean)
          }
          values={formattedData}
          sort={sort}
          onRowsSelect={onRowsSelect}
          tariff={tariffInfo?.tariff}
          tariffRiskLevel={
            riskLevel
              ? riskLevel[0] + riskLevel.slice(1).toLowerCase()
              : undefined
          }
          tariffId={tariffInfo?.tariffId}
          deleteInProgress={deleteInProgress}
          selectedFees={fees.selectedFees}
          isEditable={activePermissions?.includes(
            type === OrganizationType.INDIVIDUAL
              ? PermissionEnum.INDIVIDUAL_CUSTOMERS_EDIT_TARIFF_ACTION
              : PermissionEnum.CORPORATE_CUSTOMERS_EDIT_TARIFF_ACTION,
          )}
          onDeleteFees={() => onDelete()}
          deleteSuccess={deleteIsSuccess}
          showSidepanel={() => setPanelIsOpen(true)}
          status={
            type === OrganizationType.INDIVIDUAL
              ? baseInformation?.status
              : corporateGeneralInfo?.status
          }
        />
      )}
      <CustomMargins
        showMoreCurrencies={showMoreCurrencies}
        menuOnFocus={menuOnFocus}
        setShowMoreCurrencies={setShowMoreCurrencies}
        defaultMargin={defaultMargin}
        tariffId={tariffInfo?.tariffId}
        allMarginsVisible={allMarginsVisible}
        customerId={customerId}
        customMargins={customMargins}
        setAllMarginsVisible={setAllMarginsVisible}
        setSelectedCurrency={setSelectedCurrency}
        selectedCurrency={selectedCurrency}
        setMenuOnFocus={setMenuOnFocus}
        type={type as 'INDIVIDUAL' | 'CORPORATE'}
        customMarginsPagination={customMarginsPagination}
        setSeletedMarginId={setSeletedMarginId}
        setMarginPanelIsOpen={setMarginPanelIsOpen}
        customMarginsErrorsList={customMarginsErrorsList}
      />
      <MarginDetails
        setPanelIsOpen={setMarginPanelIsOpen}
        customerId={customerId}
        defaultMargin={defaultMargin}
        allMarginsVisible={allMarginsVisible}
        selectedCurrency={selectedCurrency}
        tariffId={tariffInfo?.tariffId}
        panelIsOpen={marginPanelIsOpen}
        selectedMarginId={selectedMarginId}
        status={
          type === OrganizationType.INDIVIDUAL
            ? (baseInformation?.status as string)
            : (corporateGeneralInfo?.status as string)
        }
      />
      <div className={cn.marginsSeparator} />
      <Outlet />
    </>
  );
};

export default CustomerTariffs;
